/**
 * Mega Menu JavaScript
 * Dawn Theme 12.0.0 Compatible
 */

class MegaMenu {
  constructor() {
    this.menuItems = document.querySelectorAll('.mega-menu__item');
    this.hoverDelay = 150;
    this.hoverTimeout = null;
    this.activeItem = null;

    this.init();
  }

  init() {
    if (this.menuItems.length === 0) return;

    this.bindDesktopEvents();
    this.bindKeyboardEvents();
    this.bindScrollEvents();
    this.bindMenuShortcut();
  }

  bindScrollEvents() {
    // Make header sticky only when user scrolls while dropdown is open
    window.addEventListener('scroll', () => {
      if (this.activeItem && this.headerSection) {
        // Enable sticky mode on first scroll with dropdown open
        if (!this.stickyEnabled) {
          this.stickyEnabled = true;
          this.headerSection.classList.add('mega-menu-sticky');
          document.body.classList.add('mega-menu-sticky-active');
        }

        // Update dropdown position
        const dropdown = this.activeItem.querySelector('.mega-menu__dropdown');
        const menuBar = this.activeItem.closest('.mega-menu');
        if (dropdown && menuBar) {
          const menuRect = menuBar.getBoundingClientRect();
          dropdown.style.top = Math.max(0, menuRect.bottom) + 'px';
        }
      }
    }, { passive: true });
  }

  bindDesktopEvents() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu__link');
      const dropdown = item.querySelector('.mega-menu__dropdown');

      if (!dropdown) return;

      // Mouse enter with delay
      item.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.openDropdown(item);
        }, this.hoverDelay);
      });

      // Mouse leave with delay
      item.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.closeDropdown(item);
        }, this.hoverDelay);
      });

      // Click to toggle (for accessibility)
      link.addEventListener('click', (e) => {
        if (dropdown) {
          e.preventDefault();
          if (item.classList.contains('mega-menu__item--active')) {
            this.closeDropdown(item);
          } else {
            this.openDropdown(item);
          }
        }
      });
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mega-menu')) {
        this.closeAllDropdowns();
      }
    });
  }

  bindKeyboardEvents() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu__link');
      const dropdown = item.querySelector('.mega-menu__dropdown');

      // Enter/Space to toggle
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (dropdown) {
            e.preventDefault();
            if (item.classList.contains('mega-menu__item--active')) {
              this.closeDropdown(item);
            } else {
              this.openDropdown(item);
              // Focus first link in dropdown
              const firstLink = dropdown.querySelector('a');
              if (firstLink) {
                setTimeout(() => firstLink.focus(), 100);
              }
            }
          }
        }

        // Arrow down to open and enter dropdown
        if (e.key === 'ArrowDown' && dropdown) {
          e.preventDefault();
          this.openDropdown(item);
          const firstLink = dropdown.querySelector('a');
          if (firstLink) {
            setTimeout(() => firstLink.focus(), 100);
          }
        }
      });

      // Escape to close
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDropdown(item);
          link.focus();
        }
      });
    });

    // Arrow navigation between top-level items
    document.addEventListener('keydown', (e) => {
      if (!e.target.closest('.mega-menu')) return;

      const currentItem = e.target.closest('.mega-menu__item');
      if (!currentItem) return;

      const items = Array.from(this.menuItems);
      const currentIndex = items.indexOf(currentItem);

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        const nextLink = items[nextIndex].querySelector('.mega-menu__link');
        this.closeAllDropdowns();
        nextLink.focus();
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        const prevLink = items[prevIndex].querySelector('.mega-menu__link');
        this.closeAllDropdowns();
        prevLink.focus();
      }
    });
  }

  openDropdown(item) {
    this.closeAllDropdowns();
    item.classList.add('mega-menu__item--active');

    const link = item.querySelector('.mega-menu__link');
    const dropdown = item.querySelector('.mega-menu__dropdown');

    link.setAttribute('aria-expanded', 'true');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');

      // Position dropdown below the menu bar
      const menuBar = item.closest('.mega-menu');
      if (menuBar) {
        const menuRect = menuBar.getBoundingClientRect();
        dropdown.style.top = menuRect.bottom + 'px';
      }

      // Stagger animation: set index on each category column + promo
      const categories = dropdown.querySelectorAll('.mega-menu__category');
      categories.forEach((cat, i) => {
        cat.style.setProperty('--stagger-index', i);
      });
      const promo = dropdown.querySelector('.mega-menu__promo');
      if (promo) {
        promo.style.setProperty('--stagger-index', categories.length);
      }

      // Store header height for potential sticky behavior on scroll
      const headerSection = document.querySelector('.section-header');
      if (headerSection) {
        const headerHeight = headerSection.offsetHeight;
        document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
        this.headerSection = headerSection;
        this.stickyEnabled = false;
      }
    }

    this.activeItem = item;
  }

  closeDropdown(item) {
    item.classList.remove('mega-menu__item--active');

    const link = item.querySelector('.mega-menu__link');
    const dropdown = item.querySelector('.mega-menu__dropdown');

    link.setAttribute('aria-expanded', 'false');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
    }

    // Remove sticky from header
    if (this.headerSection) {
      this.headerSection.classList.remove('mega-menu-sticky');
      document.body.classList.remove('mega-menu-sticky-active');
      this.stickyEnabled = false;
    }

    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  closeAllDropdowns() {
    this.menuItems.forEach(item => {
      this.closeDropdown(item);
    });
  }

  bindMenuShortcut() {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'm' && e.key !== 'M') return;
      const tag = document.activeElement.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || document.activeElement.isContentEditable) return;

      e.preventDefault();
      if (this.activeItem) {
        this.closeAllDropdowns();
      } else {
        // Open the first menu item that has a dropdown
        const firstWithDropdown = Array.from(this.menuItems).find(
          item => item.querySelector('.mega-menu__dropdown')
        );
        if (firstWithDropdown) {
          this.openDropdown(firstWithDropdown);
          firstWithDropdown.querySelector('.mega-menu__link').focus();
        }
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenu();
});

// Also initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new MegaMenu();
  });
}
